import { Check, X, Sparkles, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n";
import { blogContent } from "./content";

function Cell({
  value,
  highlight,
  warn,
  yesLabel,
  noLabel,
}: {
  value: string;
  highlight?: boolean;
  warn?: boolean;
  yesLabel: string;
  noLabel: string;
}) {
  if (value === noLabel) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground/60 text-sm">
        <X className="h-3.5 w-3.5" />
        {noLabel}
      </span>
    );
  }
  if (value.startsWith(yesLabel)) {
    return (
      <span className="inline-flex items-start gap-1.5 text-sm leading-snug">
        <Check className="h-3.5 w-3.5 mt-1 text-primary shrink-0" />
        <span className={cn(highlight ? "text-foreground font-medium" : "text-foreground/85")}>{value}</span>
      </span>
    );
  }
  if (warn) {
    return (
      <span className="inline-flex items-start gap-1.5 text-sm leading-snug">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-600 dark:text-amber-500 shrink-0" />
        <span className="text-foreground/85">{value}</span>
      </span>
    );
  }
  return (
    <span className={cn("text-sm leading-snug", highlight ? "text-foreground font-medium" : "text-foreground/80")}>
      {value}
    </span>
  );
}

export function ComparisonTable() {
  const { language } = useLanguage();
  const c = blogContent[language].comparison;
  const privacyRowIndex = 1;

  return (
    <section className="mx-auto max-w-[760px] px-4 sm:px-6 pb-12">
      <h2 className="font-['Source_Serif_4'] text-[1.75rem] sm:text-[2rem] leading-tight font-semibold text-foreground mt-4 mb-3">
        {c.title}
      </h2>
      <p className="text-sm text-muted-foreground italic mb-4">
        {c.caption}
      </p>

      <p className="md:hidden text-[11px] text-muted-foreground/60 mb-2 italic">
        {language === "fr" ? "← Faites glisser pour voir plus →" : "← Swipe to see more →"}
      </p>

      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <table className="w-full min-w-[760px] text-left border-collapse">
          <thead>
            <tr className="border-b border-border/40">
              <th className="py-4 pr-3 align-bottom text-xs font-bold uppercase tracking-wider text-muted-foreground/80 font-['Funnel_Display'] w-[20%]">
                {c.colCriteria}
              </th>
              <th className="py-4 px-3 align-bottom w-[22%] bg-primary/[0.05] border-x border-primary/10 rounded-t-md">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm font-bold tracking-tight text-primary font-['Funnel_Display']">
                    ReFound
                  </span>
                </div>
                <span className="block text-[10px] font-normal normal-case tracking-normal text-primary/60 mt-1">
                  {language === "fr" ? "L'app comparée" : "The featured app"}
                </span>
              </th>
              <th className="py-4 px-3 align-bottom text-xs font-bold tracking-tight text-foreground/75 font-['Funnel_Display'] w-[20%]">
                {c.colCashback}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground/60 mt-1">
                  {c.colCashbackSub}
                </span>
              </th>
              <th className="py-4 px-3 align-bottom text-xs font-bold tracking-tight text-foreground/75 font-['Funnel_Display'] w-[19%]">
                {c.colExpense}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground/60 mt-1">
                  {c.colExpenseSub}
                </span>
              </th>
              <th className="py-4 pl-3 align-bottom text-xs font-bold tracking-tight text-foreground/75 font-['Funnel_Display'] w-[19%]">
                {c.colScanner}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground/60 mt-1">
                  {c.colScannerSub}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {c.rows.map((row, i) => (
              <tr key={i} className="border-b border-border/20 last:border-b-0">
                <td className="py-4 pr-3 align-top font-['Source_Serif_4'] text-[15px] font-semibold text-foreground">
                  {row.feature}
                </td>
                <td className="py-4 px-3 align-top bg-primary/[0.05] border-x border-primary/10">
                  <Cell value={row.refound} highlight yesLabel={c.yes} noLabel={c.no} />
                </td>
                <td className="py-4 px-3 align-top">
                  <Cell
                    value={row.cashback}
                    warn={i === privacyRowIndex}
                    yesLabel={c.yes}
                    noLabel={c.no}
                  />
                </td>
                <td className="py-4 px-3 align-top">
                  <Cell value={row.expense} yesLabel={c.yes} noLabel={c.no} />
                </td>
                <td className="py-4 pl-3 align-top">
                  <Cell value={row.scanner} yesLabel={c.yes} noLabel={c.no} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
